import { apiFetch } from './api';

/** Pack Gratuit — 0 DZD */
export const FREE_PACK_FEATURES = [
  'Boutique en ligne professionnelle',
  "Afficher jusqu'à 15 produits",
  'Afficher 1 catégorie',
  'Afficher 1 collection',
  'Frais de transaction 0 DZD',
  'Modèles gratuits',
  'Nom de domaine gratuit (lien privé unique)',
  'Coupons de réduction',
  "Importer des données à partir d'un fichier CSV",
  'Payer par CIB',
];

/** Pack Entrepreneur — tout le Pack Gratuit + */
export const ENTREPRENEUR_PACK_FEATURES = [
  'Tout ce qui est dans le Pack Gratuit +',
  "Jusqu'à 100 produits",
  '15 catégories',
  '3 collections',
  'Frais de transaction 0 DZD',
  'Marketing par e-mail (300/jour)',
  'Marketing par SMS',
  'Domaine personnalisé',
  'Édition du code du design',
  'Import CSV',
  'Paiement CIB (El Dahabia)',
  'Support technique (Email, WhatsApp, Téléphone)',
  'Support prioritaire',
];

/** Pack Business — tout le Pack Entrepreneur + */
export const BUSINESS_PACK_FEATURES = [
  'Tout ce qui est dans le Pack Entrepreneur +',
  "Jusqu'à 500 produits",
  '50 catégories',
  '10 collections',
  'Frais de transaction 0 DZD',
  'Marketing par e-mail (600/jour)',
  'Équipe support technique dédiée',
  'Domaine personnalisé',
  'Paiement CIB (El Dahabia)',
  'Support technique (Email, WhatsApp, Téléphone)',
  'Support prioritaire',
];

export const PACK_FEATURE_LABELS = {
  Free: FREE_PACK_FEATURES,
  'Entrepreneur-mois': ENTREPRENEUR_PACK_FEATURES,
  'Entrepreneur-an': ENTREPRENEUR_PACK_FEATURES,
  'Business-mois': BUSINESS_PACK_FEATURES,
  'Business-an': BUSINESS_PACK_FEATURES,
};

const PACK_API_NAMES = {
  free: 'Free',
  entrepreneur: { monthly: 'Entrepreneur-mois', annual: 'Entrepreneur-an' },
  business: { monthly: 'Business-mois', annual: 'Business-an' },
};

const PLAN_ID_BY_UI_NAME = {
  Free: 'free',
  Entrepreneur: 'entrepreneur',
  Business: 'business',
};

function normalizeList(data) {
  if (Array.isArray(data)) return data;
  return data?.results ?? [];
}

function findPack(packs, planId, annual) {
  if (planId === 'free') {
    return packs.find((p) => p.name === PACK_API_NAMES.free) ?? null;
  }
  const apiName = annual
    ? PACK_API_NAMES[planId].annual
    : PACK_API_NAMES[planId].monthly;
  return packs.find((p) => p.name === apiName) ?? null;
}

function normalizePlanId(plan) {
  const raw = String(plan || 'free').toLowerCase();
  if (raw === 'gratuit' || raw === 'free') return 'free';
  if (raw.includes('entrepreneur')) return 'entrepreneur';
  if (raw.includes('business')) return 'business';
  return raw;
}

export { normalizePlanId };

/** GET /packager/packs/?format=json */
export async function fetchPacks() {
  const res = await apiFetch('/packager/packs/?format=json');
  if (!res.ok) {
    throw new Error('Impossible de charger les packs');
  }
  const packs = normalizeList(await res.json());
  return packs
    .filter((pack) => pack.activated !== false)
    .map((pack) => ({
      ...pack,
      featureLabels: PACK_FEATURE_LABELS[pack.name] ?? [],
    }));
}

function readCurrencyAmount(obj, currency = 'DZD') {
  if (!obj) return null;
  const value = obj[currency] ?? obj.DZD;
  return value == null ? null : Number(value);
}

/** Prix mensuel affiché (mensuel = pricing, annuel = calculated_pricing.monthly). */
export function getPlanPrice(packs, planId, { annual = false, currency = 'DZD' } = {}) {
  const pack = findPack(packs, planId, annual);
  if (!pack) return planId === 'free' ? 0 : null;
  if (planId === 'free') return 0;

  if (annual) {
    const monthly = readCurrencyAmount(pack.calculated_pricing?.monthly, currency);
    if (monthly != null) return monthly;
  }

  return readCurrencyAmount(pack.pricing, currency);
}

/** Total annuel facturé (après réduction si applicable). */
export function getPlanAnnualTotal(packs, planId, { currency = 'DZD' } = {}) {
  if (planId === 'free') return 0;
  const pack = findPack(packs, planId, true);
  if (!pack) return null;

  const discounted = readCurrencyAmount(pack.calculated_pricing?.normal, currency);
  if (discounted != null) return discounted;

  return readCurrencyAmount(pack.pricing, currency);
}

export function getPlanDiscountPercent(packs, planId) {
  const pack = findPack(packs, planId, true);
  const value = pack?.calculated_pricing?.reduction_value;
  return value == null ? null : Number(value);
}

export function getPackFeatureLabels(packName) {
  return PACK_FEATURE_LABELS[packName] ?? [];
}

function getApiPackName(planId, annual) {
  if (planId === 'free') return PACK_API_NAMES.free;
  return annual ? PACK_API_NAMES[planId].annual : PACK_API_NAMES[planId].monthly;
}

export function mergePricingPlan(basePlan, packs, { annual = false, currentPlanId = 'free' } = {}) {
  const planId = PLAN_ID_BY_UI_NAME[basePlan.name];
  if (!planId) return basePlan;

  const apiPackName = getApiPackName(planId, annual);
  const pack = findPack(packs, planId, annual);
  const price = getPlanPrice(packs, planId, { annual });
  const annualTotal = getPlanAnnualTotal(packs, planId);
  const features = pack?.featureLabels?.length
    ? pack.featureLabels
    : getPackFeatureLabels(apiPackName);

  const normalizedCurrent = normalizePlanId(currentPlanId);
  const isCurrent = normalizedCurrent === planId;

  return {
    ...basePlan,
    packId: pack?.id ?? null,
    monthlyPrice: price ?? basePlan.monthlyPrice,
    annualTotal: annualTotal ?? (annual ? Math.round((price ?? basePlan.monthlyPrice) * 12) : null),
    features: features.length ? features : basePlan.features,
    current: isCurrent,
    badge: isCurrent ? 'Actif' : basePlan.badge,
    cta: isCurrent ? 'Plan actuel' : basePlan.cta,
    disabled: isCurrent,
  };
}

export function formatPlanPrice(price, currency = 'DZD') {
  if (price == null) return '—';
  if (price === 0) return `0 ${currency}`;

  const formatted = currency === 'DZD'
    ? Math.round(price).toLocaleString('fr-DZ')
    : Number(price).toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return `${formatted} ${currency}`;
}
